import { Trans } from '@lingui/react/macro';
import { CountryCodeType } from 'noddi-util';
import { useLocation } from 'react-router-dom';
import { PAYMENT_SESSION_STATE_KEY } from '../../utils/paymentSession';
import { StripeEmbeddedSession, getStripeEmbeddedSession } from '../../utils/stripeEmbeddedSession';
import { DinteroSessionPaymentForm } from './DinteroSessionPaymentForm';
import { StripeEmbeddedPaymentForm } from './StripeEmbeddedPaymentForm';

/**
 * Provider-agnostic embedded payment form. Both providers navigate here keyed by
 * `sessionId`; the concrete widget is chosen from the Stripe embed data handed over
 * at session creation — via router `state` (reliable), falling back to a
 * sessionStorage copy so a hard refresh still works. When absent, the id is treated
 * as a Dintero session (re-embeddable from the id alone). A Stripe `cs_...` id with
 * no embed data means both channels are gone (hard refresh in a tab that never held
 * the state, or a misconfigured account); the embedded form cannot be rebuilt from
 * the id, so we surface an expired-session message.
 */
export function PaymentSessionForm({ sessionId, countryCode }: { sessionId: string; countryCode: CountryCodeType }) {
  const location = useLocation();
  const stateStripeSession = (location.state as { [PAYMENT_SESSION_STATE_KEY]?: StripeEmbeddedSession } | null)?.[
    PAYMENT_SESSION_STATE_KEY
  ];
  const stripeSession = stateStripeSession ?? getStripeEmbeddedSession(sessionId);

  if (stripeSession) {
    return (
      <StripeEmbeddedPaymentForm
        clientSecret={stripeSession.clientSecret}
        publishableKey={stripeSession.publishableKey}
        stripeAccountId={stripeSession.stripeAccountId}
      />
    );
  }

  if (sessionId.startsWith('cs_')) {
    return (
      <div className='mt-5 text-center'>
        <Trans>Your payment session has expired. Please go back and try again.</Trans>
      </div>
    );
  }

  return <DinteroSessionPaymentForm sessionId={sessionId} countryCode={countryCode} />;
}
